// Loaded from CDN and included in externals in webpack.config.js
import MathJax from "mathjax";

// Required JS from local files
import {
   closeDfmMessage,
   Context,
   dfmAlert,
   dfmDialog,
   formatVideoLength,
   getSubskillFullName
} from "./dfm5";
import {
   posthogEventSubskillVideoClosed,
   posthogEventSubskillVideoOpened
} from "~/utils/posthog/skills";

let vidId;
let vidUrl;
let vidCallback;
let allowedToPlayVideo = false;
let player;
let firstVideo = true;
let posthogData;

/**
 *
 * @param {Object} options
 * @param {Object} options.subskill - The subskill object.
 * @param {Function} options.callback - The callback function called each time the video watch data is transmitted.
 * @param {Function} options.closeCallback - The close callback function called when the video is closed.
 * @param {string} options.posthogTriggerId - The ID of the trigger for the open video event.
 * @param {Object | undefined} options.associatedTask - Any task object that is associated with watching the video.
 * @param {Object | undefined} options.associatedAttempt - Any task attempt object that is associated with watching the video.
 * @param {boolean | undefined} options.showVideoTitle - Whether to show the video title in the video popup.
 * @returns
 */
export function openVideo(options) {

   const {
      subskill,
      callback,
      closeCallback,
      posthogTriggerId,
      associatedTask,
      associatedAttempt,
      showVideoTitle = true,
   } = options ?? {};

   posthogData = {
      subskill,
      posthogTriggerId,
      associatedTask,
      associatedAttempt,
   };

   // Check if the user has accepted optional cookies.
   const cookieConsent = localStorage.getItem("cookie_consent");
   if (cookieConsent !== "optional") {
      const cookieConsentDialogMessage = `
         <h2>Cookie Consent Required</h2>
         <p>Our embedded YouTube videos use privacy-enhanced mode but optional cookies are still required for the video player to function.</p>
         <p>Please click the button below to accept optional cookies and view the video.</p>
         <p>See our <a href="/privacy.php#cookies">privacy policy</a> for more details.</p>
      `;
      const cookieConsentAcceptButton = {
         label: "Accept optional cookies",
         action: function () {
            localStorage.setItem("cookie_consent", "optional");
            $(".cookie-consent__container").hide();
            openVideo(options);
         }
      }
      dfmDialog(cookieConsentDialogMessage, [cookieConsentAcceptButton]);
      return;
   }

   allowedToPlayVideo = true;
   console.log("Opening video "+JSON.stringify(subskill));
   vidId = subskill.ssid;
   vidUrl = subskill.video;
   vidCallback = callback;
   dfmAlert(`
      ${showVideoTitle ? `<h1 class='video-title'>
         ${subskill.publicid}${subskill.letter || ""} &mdash; ${subskill.name}
      </h1>` : ""}
      <div id='video-player'></div>
      <div id='timer-info'></div>
      <div id="video-player-help-link-container">
         <a id="video-player-help-link" href="https://support.drfrost.org/hc/en-gb/articles/4641455911199-Why-are-videos-not-playing-for-me">
            Having trouble playing this video?
         </a>
      </div>
      <style>
         #video-player-help-link-container {
            display: flex;
            justify-content: center;
         }
         #video-player-help-link {
            color: #4b5563;
            font-size: 14px;
            text-decoration: underline;
            text-underline-offset: 2px;
         }
         #video-player-help-link:hover {
            color: #374151;
            text-decoration-thickness: 2px;
         }
      </style>
   `);
   $(".modal").css('padding', '15px 0px');
   MathJax.typeset();
   // The 100ms delay in loading the video because of the tiny delay in loading the
   // popup dialog container. This is quite ugly - either remove the dialog animation
   // or have some kind of callback?
   setTimeout(function(){
      $("#message-"+Context.messageCounter).on($.modal.AFTER_CLOSE, function(event, modal) {
         if (posthogTriggerId && subskill && player) {
            // Sentry reports would suggest that the methods on the player object are flaky.
            const duration = typeof player.getDuration === "function" ? player.getDuration() : null;
            const currentTime = typeof player.getCurrentTime === "function" ? player.getCurrentTime() : null;
            const statsAvailable = typeof duration === "number" && typeof currentTime === "number";
            if (statsAvailable) {
               posthogEventSubskillVideoClosed({
                  triggerId: posthogTriggerId,
                  subskill,
                  task: associatedTask,
                  attempt: associatedAttempt,
                  videoStats: {
                     duration,
                     currentTime
                  }
               });
            }
         }

         event.preventDefault();
         console.log("Closed video window");
         if(videoTimerInterval)clearInterval(videoTimerInterval);
         if(player)player.destroy();
         allowedToPlayVideo = false;
         player = undefined;
         if (typeof closeCallback === "function") {
            closeCallback();
         }
      });

      if(firstVideo) {
         var tag = document.createElement('script');
         tag.onerror = () => displayVideoError();
         tag.src = "https://www.youtube.com/iframe_api";
         var firstScriptTag = document.getElementsByTagName('script')[0];
         firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
         firstVideo = false;
      } else {
         onYouTubeIframeAPIReady();
      }

   }, 100);
}

var VIDEO_DEFAULT_WIDTH = 834;
var VIDEO_DEFAULT_HEIGHT = 471;
var requireWatch = false;

function onYouTubeIframeAPIReady() {
   const YT = window.YT;
   const useYouTubeCookies = Context?.user?._school?.use_youtube_cookies;

   if (YT) {
      var newWidth = calculateVideoWidth();
      var newHeight = newWidth * (VIDEO_DEFAULT_HEIGHT/VIDEO_DEFAULT_WIDTH);

      $("#message-"+Context.messageCounter).css('max-width', "unset");

      console.log("Creating new player "+newWidth+" "+newHeight+" "+vidUrl);

      player = new YT.Player('video-player', {
         height: newHeight,
         width: newWidth,
         playerVars: {
            origin: window.location.host,
            rel: 0,
         },
         videoId: vidUrl,
         events: {
            'onStateChange': onPlayerStateChange,
            'onReady': vidInitialise,
            'onError': displayVideoError
         },
         host: useYouTubeCookies ? 'https://www.youtube.com' : 'https://www.youtube-nocookie.com',
      });
      $(window).resize();
   } else {
      displayVideoError();
   }
}

window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;

function displayVideoError () {
   $("#video-player").empty();
   $("#video-player").append("<div style='background-color:lightgray; border-radius: 4px; padding: 64px; text-align: center;'>Sorry, there was an error loading this video.</div>");
}

/**
 * @param {Object} options
 * @param {Object[]} options.subskills
 * @param {Object} options.task
 * @param {Object} options.attempt
 * @param {boolean | undefined} options.showVideoTitle - Whether to show the video title in the video popup.
 * @returns
 */
export function videoSkillHelp(options) {
   const {
      subskills = [],
      task,
      attempt,
      showVideoTitle = true,
   } = options ?? {};

   let subskillsWithVideos = subskills.filter(function (s) {
      return s.video;
   })

   if(subskillsWithVideos.length === 0) {
      dfmAlert("Sorry, but there are no videos for this topic.");
      return;
   }

   if(subskillsWithVideos.length === 1) {
      openVideo({
         subskill: subskillsWithVideos[0],
         posthogTriggerId: "do-question-video-link",
         associatedTask: task,
         associatedAttempt: attempt,
         showVideoTitle,
      });
      return;
   }

   dfmDialog("<h2 id='requireviddialog-title'></h2><ul id='video-dialog-list'></ul>", [], true);

   $.each(subskillsWithVideos, function (k, subskill) {
      if (subskill.video) {
         $("#video-dialog-list").append("<li id='requireviddialog-vid" + subskill.ssid + "'><a href='#'><span class='video-dialog-status'><span></span><label></label></span><img src='/images/video_icon.svg'><label>" + getSubskillFullName(subskill) + "<br><small>" + formatVideoLength(subskill.videolength) + "</small></label></a></li>");
         $("#requireviddialog-vid" + subskill.ssid).data('subskill', subskill).click(function () {
            let subskill = $(this).data('subskill');
            $.modal.close();
            openVideo({
               subskill,
               closeCallback: () => videoSkillHelp(options),
               posthogTriggerId: "do-question-video-link",
               associatedTask: task,
               associatedAttempt: attempt,
               showVideoTitle,
            });
         });
      }
   });
}

function vidInitialise(event) {
   const player = event.target;
   console.log("About to initialise video");
   if(!allowedToPlayVideo)return;
   console.log("Initialise video");

   // Retrieve last viewing data (if they had previously watched it)
   $.ajax({
      url: "/api/videos/latestsession?ssid="+vidId,
      type: "GET",
      contentType:false,
      processData: false,
      cache: false,
      success: function(data){
         console.log(data);
         if(data) {
            console.log("Retrieved watch data: " + JSON.stringify(data));
            videoSession = data;
            videoTimer = data.timewatched;
            console.log("Setting watch time to "+videoTimer);
            player.seekTo(videoTimer, true);
            player.playVideo();
         } else {
            videoTimer = 0;
            videoSession = undefined;
         }

         const {
            subskill,
            posthogTriggerId,
            associatedTask,
            associatedAttempt,
         } = posthogData ?? {};

         if (posthogTriggerId && subskill) {
            posthogEventSubskillVideoOpened({
               triggerId: posthogTriggerId,
               subskill,
               task: associatedTask,
               attempt: associatedAttempt,
            });
         }
      },
      error: function(j, textStatus, errorThrown) {
         console.log("An error occurred retrieving video watch data: <strong>"+j.responseText+"</strong>");
      }
   });
}

function calculateVideoWidth() {
   if($(window).width() < 1240 && $(window).width() > 875) {
      return VIDEO_DEFAULT_WIDTH - (1240 - $(window).width());
   } else if($(window).width() <= 875) {
      return $(window).width() - 60;
   } else return VIDEO_DEFAULT_WIDTH;
}

function onPlayerStateChange(event) {
   const player = event.target;
   console.log("Player state changed");
   console.log(JSON.stringify(event.data));
   if (event.data === YT.PlayerState.PLAYING) {
      videoTimerInterval = setInterval(() => incrementTime(player), 1000);
   } else {
      console.log("Stopped video");
      clearInterval(videoTimerInterval);
   }
}

$(window).resize(function(){
   var newWidth = calculateVideoWidth();
   var newHeight = newWidth * (VIDEO_DEFAULT_HEIGHT/VIDEO_DEFAULT_WIDTH);
   if(player)player.setSize(newWidth, newHeight);
   $("#message-"+Context.messageCounter).css('width', (0+newWidth)+"px");
});

var videoTimer = 0;
var videoTimerInterval;
function incrementTime(player) {
   videoTimer++; // totalVideoTime++;
   if(Context.user) {
      var hours = Math.floor(videoTimer / 3600);
      var minutes = Math.floor(videoTimer / 60) % 60;
      var seconds = videoTimer % 60;
      var timeStr = seconds + "s";
      if(hours!=0 || minutes!=0)timeStr = minutes + "m "+timeStr;
      if(hours!=0)timeStr = hours + "h "+timeStr;
      if(requireWatch)timeStr+= " - You need to watch this video to the end. Do not skip portions of the video."; 
      $("#timer-info").html(timeStr);
      if (videoTimer % 5 === 0) {
         transmitViewInfo(player);
      }
   }
}

var videoSession;

function transmitViewInfo(player) {
   let data = {
      ssid: vidId,
      time: videoTimer,
      fromStart: player.getCurrentTime(),
      duration: Math.floor(player.getDuration()),
      timeWatched: videoTimer
   }

   if (videoSession) {
      data.vwid = videoSession.vwid;
      data.ssid = null;
   }

   $.ajax({
      url: "/api/videos/logwatch",
      type: "POST",
      contentType: false,
      processData: false,
      cache: false,
      data: JSON.stringify(data),
      dataType: "json",
      success: function (data) {
         if (!videoSession) videoSession = data;
         if (vidCallback) vidCallback(data);
      },
      error: function (j, textStatus, errorThrown) {
         console.log("An error occurred logging video watch data: <strong>" + j.responseText + "</strong>");
      }
   });
}
